import {
  AnyApiFactory,
  createApiFactory,
  featureFlagsApiRef,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  ExtensionDefinition,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { Confidence, visitorIdentity } from '@spotify-confidence/sdk';
import { imageUploadApiRef } from '@spotify/confidence-backstage-image-upload';
import {
  BUILD_COMMIT_HASH,
  ConfidenceAuth,
  ConfidenceImageUploadClient,
  authOverrideStore,
  confidenceUserApiRef,
  createEventBlockingFetchImplementation,
  createReadOnlyFetchImplementation,
  getFlagResolverProxyOptions,
  getSessionId,
} from '@spotify/confidence-core-react';
import {
  ConfidenceFeatureFlags,
  bindOpenFeature,
} from '@spotify/confidence-flag-evaluation-react';
import { CreateMeasurementDialog } from '@spotify/plugin-confidence-metrics';
import { createMeasurementDialogExtensionsApiRef } from '@spotify/plugin-confidence-metrics-react';
import { TrackingConfidenceEvents } from '@spotify/plugin-confidence-tracking-module-confidence-events';
import {
  DevConsoleTracking,
  trackingApiRef,
} from '@spotify/plugin-confidence-tracking-react';
import { statsConfigApiRef } from '@spotify/plugin-confidence-workflows-module-stats';

const getIdleLogger = () => {
  const idle = () => {};

  return ['info', 'warn', 'error', 'trace', 'debug'].reduce<
    Record<string, () => void>
  >(
    (acc, method) => ({
      ...acc,
      [method]: idle,
    }),
    {},
  );
};
export const confidenceApi = Confidence.create({
  clientSecret: 'NiAgIVmkjVSTNI5k8mpsgs3EeFqkacrA',
  environment: 'client',
  timeout: 3000,
  logger: getIdleLogger(),
  context: {
    session_id: getSessionId(),
    build_version: BUILD_COMMIT_HASH ?? 'dev',
  },
  fetchImplementation: createReadOnlyFetchImplementation(
    () => !!authOverrideStore.get(),
    createEventBlockingFetchImplementation(
      () => process.env.NODE_ENV === 'development',
    ),
  ),
  ...getFlagResolverProxyOptions(
    window.location.hostname,
    window.location.origin,
  ),
});

confidenceApi.track(visitorIdentity({ domain: '.confidence.spotify.com' }));

bindOpenFeature(confidenceApi);

const apis: AnyApiFactory[] = [
  createApiFactory({
    api: statsConfigApiRef,
    deps: {},
    factory: () => ({
      powerPresets: [
        {
          title: 'High - 90%',
          description: 'Increased sensitivity and reproducibility',
          value: 0.9,
        },
        {
          title: 'Standard - 80%',
          description: 'The standard recommendation for a typical A/B test',
          value: 0.8,
        },
      ],
    }),
  }),
  createApiFactory({
    api: catalogApiRef,
    deps: {},
    // Stub: only getEntityByRef is used by Backstage internals in this app
    factory: () =>
      ({ getEntityByRef() {} }) as unknown as typeof catalogApiRef.T,
  }),
  createApiFactory({
    api: confidenceUserApiRef,
    deps: {},
    factory: () => ConfidenceAuth.create(),
  }),
  createApiFactory({
    api: featureFlagsApiRef,
    deps: {},
    factory: () => new ConfidenceFeatureFlags(),
  }),
  createApiFactory({
    api: imageUploadApiRef,
    deps: {
      userApi: confidenceUserApiRef,
    },
    factory: ({ userApi }) => new ConfidenceImageUploadClient(userApi),
  }),
  createApiFactory({
    api: trackingApiRef,
    deps: {},
    factory: () =>
      process.env.NODE_ENV === 'development'
        ? new DevConsoleTracking()
        : new TrackingConfidenceEvents(confidenceApi),
  }),
  createApiFactory({
    api: createMeasurementDialogExtensionsApiRef,
    deps: {},
    factory: () => ({ component: CreateMeasurementDialog }),
  }),
];

export const AppApis: ExtensionDefinition[] = apis.map(factory =>
  ApiBlueprint.make({
    name: factory.api.id,
    params: defineParams => defineParams(factory),
  }),
);
